import type { ProgramSlim } from 'models';
import { useTranslation } from 'react-i18next';
import { DataCard, Icon } from 'ui';
import { images } from 'common/utils';

type P = {
  getPathToProgramDetail: (id: string) => string;
  program: ProgramSlim;
  showPrice?: boolean;
  showTag?: boolean;
};

const ProgramCard = ({ getPathToProgramDetail, program, showPrice = true, showTag = true }: P) => {
  const { t } = useTranslation();
  const calcProgramPrice = (): { currentPrice: number; isFree: boolean; originalPrice: number | undefined } => {
    const originalPrice = Number.parseFloat(program.price ?? '0');
    const paidBySponsors =
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      program.sponsors?.reduce((previous, current) => previous + Number.parseFloat(current.price ?? '0'), 0) ?? 0;
    const currentPrice = Math.max(originalPrice - paidBySponsors, 0);
    const isFree = currentPrice === 0;
    const isSponsoredFree = isFree && paidBySponsors > 0;

    return {
      currentPrice: isSponsoredFree ? paidBySponsors : currentPrice,
      isFree: isFree || isSponsoredFree,
      originalPrice: originalPrice === currentPrice ? undefined : originalPrice,
    };
  };

  return (
    <DataCard
      className="program-entity-card"
      description={{ dateFrom: program.startTime, dateTo: program.endTime }}
      image={{
        alt: 'Program cover image',
        src: program.coverPhoto?.url ?? images.default.programCoverPhoto,
      }}
      participantsProperties={{
        count: program.membersCount,
        label: (formattedMembersCount) => t('{{participants}} enrolled', { participants: formattedMembersCount }),
      }}
      path={getPathToProgramDetail(program.id)}
      pricesProperties={
        showPrice
          ? {
              currentPrice: calcProgramPrice().currentPrice,
              isFree: calcProgramPrice().isFree,
              originalPrice: calcProgramPrice().originalPrice,
            }
          : undefined
      }
      tag={showTag ? { children: 'Program', color: 'blue', icon: <Icon icon="mortarboard-line" /> } : undefined}
      title={program.name}
    />
  );
};

export default ProgramCard;
